import servicesData from '@/data/services-knowledge.json'

/**
 * Single source of truth for service pricing.
 *
 * Both the hero "From $X" display string AND the JSON-LD offer derive from the
 * same `starting_price` field in services-knowledge.json. This makes the old
 * bug category impossible: the displayed price and the structured-data price
 * can no longer drift apart, and AI engines quote a number that matches the page.
 */

export type ServicePriceKey =
  | 'ai-product-development'
  | 'ai-powered-growth'
  | 'revenue-first-design'
  | 'ai-visibility-strategy'

interface UnitPriceSpec {
  '@type': 'UnitPriceSpecification'
  price: string
  priceCurrency: 'USD'
  referenceQuantity: {
    '@type': 'QuantitativeValue'
    value: '1'
    unitText: 'MONTH'
  }
}

export interface ServiceOffer {
  '@type': 'Offer'
  price: string
  priceCurrency: 'USD'
  availability: string
  url: string
  priceSpecification?: UnitPriceSpec
}

export interface ServicePriceModel {
  /** Hero display string, e.g. "From $4,995" or "From $2,995/mo". */
  heroPrice: string
  /** schema.org Offer built from the same source — never drifts from heroPrice. */
  offer: ServiceOffer
  monthly: boolean
}

/** Pull the first USD figure out of a price string (ignores PHP ₱ amounts). */
function extractUsd(priceString: string): number | null {
  const match = priceString.match(/\$\s?([\d,]+)/)
  if (!match) return null
  return parseInt(match[1].replace(/,/g, ''), 10)
}

function isMonthly(priceString: string): boolean {
  return /\/mo|\/month/i.test(priceString)
}

export function getServicePriceModel(
  serviceType: ServicePriceKey,
  url: string
): ServicePriceModel | null {
  const service = (
    servicesData.services as Record<string, { starting_price?: string }>
  )[serviceType]
  const startingPrice = service?.starting_price
  if (!startingPrice) return null

  const usd = extractUsd(startingPrice)
  if (usd == null) return null

  const monthly = isMonthly(startingPrice)
  const heroPrice = `From $${usd.toLocaleString('en-US')}${monthly ? '/mo' : ''}`

  const offer: ServiceOffer = {
    '@type': 'Offer',
    price: String(usd),
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    url,
  }

  if (monthly) {
    offer.priceSpecification = {
      '@type': 'UnitPriceSpecification',
      price: String(usd),
      priceCurrency: 'USD',
      referenceQuantity: {
        '@type': 'QuantitativeValue',
        value: '1',
        unitText: 'MONTH',
      },
    }
  }

  return { heroPrice, offer, monthly }
}

/**
 * USD price string for a pricing-tier card. Growth tiers store PHP in `price`
 * and the USD equivalent in `price_usd`; every other service stores USD in
 * `price`. Normalizes away the "~" and trailing " USD" for clean display.
 */
export function getUsdPriceString(pkg: {
  price: string
  price_usd?: string
}): string {
  const raw = pkg.price_usd ?? pkg.price
  return raw
    .replace(/~/g, '')
    .replace(/\s*USD\s*/gi, '')
    .trim()
}
